'use client';

import React, {createContext, ReactNode, useContext, useMemo, useState} from 'react';
import {
    AssetStatus,
    AssetVideo as GraphQLAssetVideo,
    CoursePromoVideo,
    MediaSourceType,
    useLecturesQuery,
} from '@/generated/graphql';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {VideoPlayerProvider} from '@/features/course-landing-page/components/video-player/video-player-context';
import {PreviewableLecture} from '@/types/lecture';
import {useCoursePromoVideo} from '@/features/course-landing-page/hooks/use-course-promo-video';

// Hardcoded ID used to represent the promo video in our preview flow - this allows us to inject it as a fake lecture item
export const PROMO_VIDEO_ID = 'promo-video';

interface CoursePreviewVideoProviderProps {
    children: ReactNode;
}

interface CoursePreviewVideoContextValue {
    previewableAssetsByLectureId: Map<string, GraphQLAssetVideo>;
    previewableLectureById: Map<string, PreviewableLecture>;
    selectedPreviewLectureId?: string;
    setSelectedPreviewLectureId: (lectureId: string, callback?: () => void) => void;
    /** Indicates if we're using promo video as fallback (no previewable lectures available) */
    isUsingPromoVideoFallback: boolean;
}

const CoursePreviewVideoContext = createContext<CoursePreviewVideoContextValue | undefined>(
    undefined,
);

/**
 * Creates a mock AssetVideo from promoVideo data to integrate promo video into the regular preview flow.
 * The promo video is treated as the first previewable lecture with ID PROMO_VIDEO_ID for tracking purposes.
 */
function createPromoVideoAsset(promoVideo: Omit<CoursePromoVideo, 'asset'>): GraphQLAssetVideo {
    return {
        __typename: 'AssetVideo',
        id: PROMO_VIDEO_ID, // Use a consistent ID for the promo video
        title: 'Course Preview',
        duration: 0, // Default duration since promoVideo doesn't provide it
        status: AssetStatus.Success,
        isDrmProtected: false,
        accessToken: null,
        captions: [],
        downloads: [],
        mediaSources: [
            {
                __typename: 'AssetMediaSource',
                mediaType: MediaSourceType.VideoMp4,
                src: promoVideo.url,
                label: 'auto',
            },
        ],
        processingErrors: null,
        thumbnailTrack: null,
    };
}

/**
 * Provider that fetches video data for all previewable lectures from the course curriculum
 * and provides context for selecting which lecture to preview. Wraps children with VideoPlayerProvider
 * for the currently selected lecture.
 */
export function CoursePreviewVideoProvider({children}: CoursePreviewVideoProviderProps) {
    const course = useClpCourse();

    // Fetch promo video data using custom hook with TTL-based refetching
    const {promoVideo: graphqlPromoVideo} = useCoursePromoVideo({
        id: course.id,
    });

    // Get promo video, preferring GraphQL data over potentially stale course data
    const promoVideo = useMemo(() => {
        return graphqlPromoVideo || course?.promoVideo || null;
    }, [graphqlPromoVideo, course?.promoVideo]);

    // Find all previewable video lectures from the curriculum using useMemo
    const previewableLectureIds = useMemo(() => {
        const curriculumLectureIds: string[] = [];
        const curriculum = course.curriculum;
        const sections = curriculum?.sections;

        if (sections) {
            // Find all previewable lectures from curriculum
            curriculumLectureIds.push(
                ...sections
                    .flatMap((section) => {
                        return section.items;
                    })
                    .filter((item) => {
                        // Check if it's a video lecture with isPreviewable = true
                        return (
                            'isPreviewable' in item &&
                            item.isPreviewable &&
                            'durationInSeconds' in item &&
                            typeof item.durationInSeconds === 'number'
                        );
                    })
                    .map((item) => item.id),
            );
        }

        // If course has promo video, add it as the first previewable lecture
        if (promoVideo?.url) {
            return [PROMO_VIDEO_ID, ...curriculumLectureIds];
        }

        return curriculumLectureIds;
    }, [course.curriculum, promoVideo]);

    // State to track which lecture is currently selected for preview
    const [selectedPreviewLectureId, setSelectedPreviewLectureIdState] = useState<
        string | undefined
    >(previewableLectureIds[0]);

    // Wrapper function that supports optional callback
    const setSelectedPreviewLectureId = React.useCallback(
        (lectureId: string, callback?: () => void) => {
            setSelectedPreviewLectureIdState(lectureId);
            // Execute callback optimistically after state update
            if (callback) {
                callback();
            }
        },
        [],
    );

    // Update selected lecture when previewable lectures change
    React.useEffect(() => {
        if (previewableLectureIds.length > 0 && !selectedPreviewLectureId) {
            setSelectedPreviewLectureIdState(previewableLectureIds[0]);
        }
    }, [previewableLectureIds, selectedPreviewLectureId]);

    // Filter out promo-video ID for the GraphQL query since it's not a real lecture
    const curriculumLectureIds = useMemo(() => {
        return previewableLectureIds.filter((id) => id !== PROMO_VIDEO_ID);
    }, [previewableLectureIds]);

    // Fetch lecture details - refetch when selected lecture changes
    const {data: lectureData, refetch} = useLecturesQuery(
        {ids: curriculumLectureIds},
        {
            enabled: curriculumLectureIds.length > 0,
        },
    );

    // Refetch lecture data whenever the selected lecture changes (only for curriculum lectures)
    React.useEffect(() => {
        if (
            selectedPreviewLectureId &&
            selectedPreviewLectureId !== PROMO_VIDEO_ID &&
            curriculumLectureIds.length > 0
        ) {
            refetch();
        }
    }, [selectedPreviewLectureId, refetch, curriculumLectureIds.length]);

    // Create memoized data structures for previewable lectures and assets
    const {previewableAssetsByLectureId, previewableLectureById} = useMemo(() => {
        const assetsByLectureId = new Map<string, GraphQLAssetVideo>();
        const lectureById = new Map<string, PreviewableLecture>();

        // Add promo video data if it exists
        if (promoVideo?.url) {
            const promoAsset = createPromoVideoAsset(promoVideo);
            assetsByLectureId.set(PROMO_VIDEO_ID, promoAsset);
            const thumbnail = course.images?.height125 || null;

            // Create a minimal lecture object for the promo video with only required fields
            const promoLecture: PreviewableLecture = {
                id: PROMO_VIDEO_ID,
                title: course.title,
                durationInSeconds: promoAsset.duration,
                thumbnail: thumbnail,
                asset: promoAsset,
            };

            lectureById.set(PROMO_VIDEO_ID, promoLecture);
        }

        // Add curriculum lectures
        if (lectureData?.lectures) {
            lectureData.lectures.forEach((lecture) => {
                if (!lecture) return;

                if (
                    lecture.__typename === 'VideoLecture' &&
                    'asset' in lecture &&
                    lecture.asset?.__typename === 'AssetVideo'
                ) {
                    // Create a PreviewableLecture with only the required fields
                    const previewableLecture: PreviewableLecture = {
                        id: lecture.id,
                        title: lecture.title,
                        durationInSeconds: lecture.durationInSeconds,
                        thumbnail: lecture.thumbnail,
                        asset: lecture.asset as GraphQLAssetVideo,
                    };

                    // Store the lecture data
                    lectureById.set(lecture.id || '', previewableLecture);

                    // Store the asset data
                    assetsByLectureId.set(lecture.id || '', lecture.asset as GraphQLAssetVideo);
                }
            });
        }

        return {
            previewableAssetsByLectureId: assetsByLectureId,
            previewableLectureById: lectureById,
        };
    }, [lectureData, course, promoVideo]);

    // Determine if we should use promo video fallback (this should now be rare since promo video is integrated)
    const isUsingPromoVideoFallback = useMemo(() => {
        return previewableLectureIds.length === 0;
    }, [previewableLectureIds.length]);

    // Get the currently selected asset and duration
    const {asset, durationInSeconds} = useMemo(() => {
        if (!selectedPreviewLectureId) {
            return {asset: undefined, durationInSeconds: 0};
        }

        const selectedAsset = previewableAssetsByLectureId.get(selectedPreviewLectureId);
        const selectedLecture = previewableLectureById.get(selectedPreviewLectureId);

        if (selectedAsset && selectedLecture) {
            return {
                asset: selectedAsset,
                durationInSeconds: selectedLecture.durationInSeconds || 0,
            };
        }

        return {asset: undefined, durationInSeconds: 0};
    }, [selectedPreviewLectureId, previewableAssetsByLectureId, previewableLectureById]);

    // Create context value
    const contextValue: CoursePreviewVideoContextValue = {
        previewableAssetsByLectureId,
        previewableLectureById,
        selectedPreviewLectureId,
        setSelectedPreviewLectureId,
        isUsingPromoVideoFallback,
    };

    return (
        <CoursePreviewVideoContext.Provider value={contextValue}>
            <VideoPlayerProvider asset={asset} durationInSeconds={durationInSeconds}>
                {children}
            </VideoPlayerProvider>
        </CoursePreviewVideoContext.Provider>
    );
}

/**
 * Hook to access the course preview video context - to avoid breaking encapsulation, should only be
 * used interally by the hooks that are exported from this file.
 */
function useCoursePreviewVideoContext() {
    const context = useContext(CoursePreviewVideoContext);
    if (!context) {
        throw new Error(
            'useCoursePreviewVideoContext must be used within a CoursePreviewVideoProvider',
        );
    }
    return context;
}

/**
 * Hook to set the selected preview lecture ID
 */
export function useSetSelectedPreviewLectureId() {
    const {setSelectedPreviewLectureId} = useCoursePreviewVideoContext();
    return setSelectedPreviewLectureId;
}

/**
 * Hook to check if a lecture ID is currently selected for preview
 */
export function useIsCurrentPreview(lectureId: string) {
    const {selectedPreviewLectureId} = useCoursePreviewVideoContext();
    return selectedPreviewLectureId === lectureId;
}

/**
 * Hook to check if we're currently using promo video as fallback
 */
export function useIsUsingPromoVideoFallback() {
    const {isUsingPromoVideoFallback} = useCoursePreviewVideoContext();
    return isUsingPromoVideoFallback;
}

/**
 * Hook to get all previewable lectures including promo video (if available)
 */
export function usePreviewableLectures() {
    const {previewableLectureById} = useCoursePreviewVideoContext();
    const course = useClpCourse();

    // Get promo video data using the same custom hook as the provider
    const {promoVideo: graphqlPromoVideo} = useCoursePromoVideo({
        id: course.id,
    });

    const promoVideo = useMemo(() => {
        return graphqlPromoVideo || course?.promoVideo || null;
    }, [graphqlPromoVideo, course?.promoVideo]);

    const curriculum = course?.curriculum;
    return useMemo(() => {
        // Get the previewable lecture IDs in the correct order (promo video first if available)
        const lectureIds: string[] = [];

        // Add promo video first if it exists
        if (promoVideo?.url) {
            lectureIds.push(PROMO_VIDEO_ID);
        }

        // Add curriculum lectures
        const sections = curriculum?.sections;
        if (sections) {
            sections
                .flatMap((section) => section.items)
                .filter((item) => {
                    return (
                        'isPreviewable' in item &&
                        item.isPreviewable &&
                        'durationInSeconds' in item &&
                        typeof item.durationInSeconds === 'number'
                    );
                })
                .forEach((item) => {
                    lectureIds.push(item.id);
                });
        }

        // Return lectures in the correct order, filtering out any that aren't in our map
        return lectureIds
            .map((id) => previewableLectureById.get(id))
            .filter((lecture): lecture is PreviewableLecture => lecture != null);
    }, [previewableLectureById, promoVideo, curriculum]);
}
